import React, { useState } from "react";
import InputField from "../../component/inputfiled/Inputfiled";
import { Form, Formik } from "formik";
import CustomButton from "../../component/button/button";
import OutlineCustomButton from "../../component/button/outlineButton";
import { Box, Paper, Grid, Typography, Button, CircularProgress } from "@mui/material";
import axios from 'axios';
import { basicurl } from "../../Redux/ServiceUrl/url";
import { fontSize } from "../../component/theme/fontSize";
import { fontWeight } from "../../component/theme/fontWeight";
import { FontFamily } from "../../component/theme/fontFamily";
import { ThemeColor } from "../../component/theme/color";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../Redux/action/SnackbarAction";

function ForgotModule() {
  const [inputValue, setInputValue] = useState("");
  const [stage, setStage] = useState('request'); // 'request' | 'reset'
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleOtpChange = (e) => setOtp(e.target.value);
  const handlePasswordChange = (e) => setPassword(e.target.value);
  const handleConfirmPasswordChange = (e) => setConfirmPassword(e.target.value);

  // Step 1: request OTP
  const handleRequestReset = async () => {
    if (!inputValue) {
      dispatch(showSnackbar('Please enter your email', 'error'));
      return;
    }
    // show sending snackbar
    dispatch(showSnackbar('Sending OTP...', 'info'));
    setLoading(true);
    try {
      const res = await axios.post(`${basicurl}/api/login/request-password-reset`, { email: inputValue });
      if (res.status === 200) {
        dispatch(showSnackbar(res.data?.message || 'OTP sent to your email', 'success'));
        setStage('reset');
      } else {
        dispatch(showSnackbar(res.data?.message || 'Failed to request reset', 'error'));
      }
    } catch (err) {
      console.error('request-password-reset error', err);
      dispatch(showSnackbar(err.response?.data?.message || 'Failed to request reset', 'error'));
    } finally {
      setLoading(false);
    }
  };

  // Step 2: verify OTP and reset password
  const handleVerifyAndReset = async () => {
    if (!otp) return dispatch(showSnackbar('Please enter the OTP', 'error'));
    if (!password) return dispatch(showSnackbar('Please enter a new password', 'error'));
    if (password !== confirmPassword) return dispatch(showSnackbar('Passwords do not match', 'error'));
    // show verifying snackbar
    dispatch(showSnackbar('Verifying OTP and resetting password...', 'info'));
    setLoading(true);
    try {
      const payload = { email: inputValue, otp: otp, password: password };
      const res = await axios.post(`${basicurl}/api/login/verify-otp-and-reset-password`, payload);
      if (res.status === 200) {
        dispatch(showSnackbar(res.data?.message || 'Password reset successful', 'success'));
        setTimeout(() => navigate('/'), 1200);
      } else {
        dispatch(showSnackbar(res.data?.message || 'Failed to reset password', 'error'));
      }
    } catch (err) {
      console.error('verify-otp-and-reset-password error', err);
      dispatch(showSnackbar(err.response?.data?.message || 'Failed to reset password', 'error'));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        px: { xs: 2, sm: 3 },
        py: { xs: 4, sm: 6 },
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: '100%',
          maxWidth: 520,
          borderRadius: 3,
          p: { xs: 2.5, sm: 4 },
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Typography fontSize={fontSize.h2.medium} fontWeight={fontWeight.W700} fontFamily={FontFamily.Nunito} color={ThemeColor.NAVYBLUE}>
              Forgot password
            </Typography>
            <Typography fontSize={fontSize.h4.small} fontWeight={fontWeight.W400} fontFamily={FontFamily.Nunito} color="text.secondary" sx={{ mt: 1 }}>
              Enter your account email and we'll send a link to reset your password.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            {stage === 'request' ? (
              <>
                <InputField
                  value={inputValue}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  fullWidth={true}
                />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                    gap: { xs: 1, sm: 2 },
                    justifyContent: 'flex-end',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    mt: 2,
                    '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } },
                  }}
                >
                  <Button variant="text" onClick={() => navigate('/')} sx={{ color: ThemeColor.GREY }}>Back to login</Button>
                  <CustomButton variant="contained" backgroundColor={ThemeColor.LIGHTBLUE} color={ThemeColor.NAVYBLUE} onClick={handleRequestReset} disabled={loading}>
                    {loading ? <CircularProgress size={18} color="inherit" /> : 'Send OTP'}
                  </CustomButton>
                </Box>
              </>
            ) : (
              <>
                <InputField value={inputValue} onChange={handleInputChange} placeholder="Email" fullWidth={true} disabled />
                <Box sx={{ mt: 1 }}>
                  <InputField value={otp} onChange={handleOtpChange} placeholder="Enter OTP" fullWidth={true} />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <InputField value={password} onChange={handlePasswordChange} placeholder="New password" fullWidth={true} type="password" />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <InputField value={confirmPassword} onChange={handleConfirmPasswordChange} placeholder="Confirm new password" fullWidth={true} type="password" />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                    gap: { xs: 1, sm: 2 },
                    justifyContent: 'flex-end',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    mt: 2,
                    '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } },
                  }}
                >
                  <Button variant="text" onClick={() => setStage('request')} sx={{ color: ThemeColor.GREY }}>Back</Button>
                  <CustomButton variant="contained" backgroundColor={ThemeColor.LIGHTBLUE} color={ThemeColor.NAVYBLUE} onClick={handleVerifyAndReset} disabled={loading}>
                    {loading ? <CircularProgress size={18} color="inherit" /> : 'Reset Password'}
                  </CustomButton>
                </Box>
              </>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">If you don't receive an email within a few minutes, check your spam folder or try again.</Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default ForgotModule;
